/**
 * Studio — shared scaffolding for Blog Studio and Document Studio.
 *
 * Exports:
 *   · API_BASE / STORAGE_ADMIN  — shared constants
 *   · Tabs                      — the 5-tab cross-nav (Access · Calendar ·
 *                                  Blog · Documents · Admins)
 *   · StatusBadge               — draft/review/published chip
 *   · RefineBar                 — instruction textarea + Refine button +
 *                                  inline error
 *   · RevisionDrawer            — slide-down list of named revisions with
 *                                  "Restore" buttons
 *   · BriefField                — labelled label-over-input wrapper
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, History, RotateCcw } from "lucide-react";
import { MicField } from "./MicField";

export const API_BASE = process.env.REACT_APP_BACKEND_URL;
export const STORAGE_ADMIN = "hv_admin_token";

const STATUS_COLOURS = {
  draft:     { bg: "rgba(194,146,46,0.12)", border: "rgba(194,146,46,0.40)", fg: "#8A6914" },
  review:    { bg: "rgba(63,128,191,0.12)", border: "rgba(63,128,191,0.40)", fg: "#1E4E7A" },
  published: { bg: "rgba(31,182,193,0.18)", border: "rgba(31,182,193,0.45)", fg: "#0E7A82" },
};

export function StatusBadge({ status }) {
  const c = STATUS_COLOURS[status] || STATUS_COLOURS.draft;
  return (
    <span
      className="px-2 py-0.5 font-mono uppercase"
      style={{
        background: c.bg, border: `1px solid ${c.border}`,
        color: c.fg, fontSize: "0.58rem", letterSpacing: "0.18em",
        fontWeight: 700, borderRadius: 3,
      }}
      data-testid={`status-badge-${status}`}
    >
      {status}
    </span>
  );
}

const TABS = [
  { to: "/portal/content-planner",        label: "Compose",          key: "compose" },
  { to: "/portal/content-planner/calendar", label: "Calendar",       key: "calendar" },
  { to: "/portal/workspace/ideas",        label: "Ideas",            key: "ideas" },
  { to: "/portal/content-studio/blog",    label: "Blog",             key: "blog" },
  { to: "/portal/content-studio/docs",    label: "PDFs",             key: "docs" },
  { to: "/portal/workspace/trash",        label: "Trash",            key: "trash" },
];

export function Tabs({ current }) {
  return (
    <div className="flex border-b mb-6 flex-wrap"
         style={{ borderColor: "rgba(12,42,64,0.12)" }}
         data-testid="studio-tabs">
      {TABS.map((t) => {
        const active = t.key === current;
        if (active) {
          return (
            <span key={t.key}
                  data-testid={`studio-tab-${t.key}-current`}
                  className="px-4 py-2.5 font-mono uppercase"
                  style={{ fontSize: "0.65rem", letterSpacing: "0.18em",
                           fontWeight: 700, color: "#0C2A40",
                           borderBottom: "2px solid #1FB6C1" }}>
              {t.label}
            </span>
          );
        }
        return (
          <Link key={t.key} to={t.to}
                data-testid={`studio-tab-${t.key}`}
                className="px-4 py-2.5 font-mono uppercase"
                style={{ fontSize: "0.65rem", letterSpacing: "0.18em",
                         fontWeight: 700, color: "rgba(12,42,64,0.60)",
                         borderBottom: "2px solid transparent",
                         textDecoration: "none" }}>
            {t.label}
          </Link>
        );
      })}
    </div>
  );
}

export function BriefField({ label, hint, children, testid }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="font-mono uppercase mb-1"
           style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                    color: "rgba(12,42,64,0.65)", fontWeight: 700 }}>
        {label}
      </div>
      {children}
      {hint && (
        <div className="mt-1 font-light"
             style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)",
                      lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function RefineBar({ onRefine, busy, error, testidPrefix, apiToken }) {
  const [instr, setInstr] = useState("");
  return (
    <div className="hv-card-soft"
         style={{ padding: 14, background: "rgba(31,182,193,0.06)",
                  borderColor: "rgba(31,182,193,0.30)" }}
         data-testid={`${testidPrefix}-refine-bar`}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} style={{ color: "#0E7A82" }} />
        <span className="font-mono uppercase"
              style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                       color: "#0E7A82", fontWeight: 700 }}>
          Refine with AI — polish, restructure, or rewrite
        </span>
      </div>
      <MicField value={instr} onChange={setInstr} apiToken={apiToken}
                testid={`${testidPrefix}-refine-mic`}>
        <textarea
          value={instr}
          onChange={(e) => setInstr(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && instr.trim()) {
              onRefine(instr).then(() => setInstr(""));
            }
          }}
          rows={3}
          placeholder={
            "Tell the AI what to change — e.g.\n" +
            "  • \"trim the intro, lead with the SANS 241 point\"\n" +
            "  • \"add a section comparing MBR vs MBBR for 30-suite lodges\"\n" +
            "  • \"make it shorter and more declarative\""
          }
          className="hv-input w-full"
          style={{ resize: "vertical", fontSize: "0.88rem" }}
          data-testid={`${testidPrefix}-refine-input`}
        />
      </MicField>
      <div className="mt-2 flex items-center gap-2">
        <button type="button"
                onClick={() => onRefine(instr).then(() => setInstr(""))}
                disabled={busy || !instr.trim()}
                data-testid={`${testidPrefix}-refine-submit`}
                className="hv-pill is-active">
          <Sparkles size={11} />
          {busy ? "Refining…" : "Refine"}
        </button>
        <span className="font-mono uppercase ml-auto"
              style={{ fontSize: "0.58rem", letterSpacing: "0.16em",
                       color: "rgba(12,42,64,0.5)" }}>
          ⌘ / Ctrl + Enter
        </span>
      </div>
      {error && (
        <div className="mt-2 font-mono"
             style={{ fontSize: "0.75rem", color: "#B85C38" }}
             data-testid={`${testidPrefix}-refine-error`}>
          {error}
        </div>
      )}
    </div>
  );
}

export function RevisionDrawer({ revisions, onRestore, busy, testidPrefix }) {
  const [open, setOpen] = useState(false);
  const list = revisions || [];
  return (
    <div className="hv-card-soft" style={{ padding: 12 }}
         data-testid={`${testidPrefix}-revisions`}>
      <button type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid={`${testidPrefix}-revisions-toggle`}
              className="w-full flex items-center justify-between"
              style={{ background: "transparent", border: "none",
                       cursor: "pointer", padding: 0 }}>
        <div className="flex items-center gap-2">
          <History size={14} style={{ color: "#0E7A82" }} />
          <span className="font-mono uppercase"
                style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                         color: "#0E7A82", fontWeight: 700 }}>
            Revision history — {list.length}
          </span>
        </div>
        <span className="font-mono uppercase"
              style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                       color: "rgba(12,42,64,0.55)" }}>
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open && list.length === 0 && (
        <div className="mt-3 font-light"
             style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.55)" }}>
          No revisions yet — every Refine, manual edit, or restore adds one here.
        </div>
      )}
      {open && list.length > 0 && (
        <ul className="mt-3 space-y-2">
          {list.slice().reverse().map((r, idx) => {
            const ordinalIdx = list.length - idx;
            return (
              <li key={r.id}
                  className="flex items-center justify-between gap-2"
                  style={{ padding: "8px 10px",
                           background: "#FFFFFF",
                           border: "1px solid rgba(12,42,64,0.10)",
                           borderRadius: 8 }}
                  data-testid={`${testidPrefix}-revision-${r.id}`}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: "0.85rem", color: "#0C2A40",
                                fontWeight: 500 }}>
                    v{ordinalIdx} · {r.note || "(no note)"}
                  </div>
                  <div className="font-mono"
                       style={{ fontSize: "0.65rem",
                                color: "rgba(12,42,64,0.55)" }}>
                    {new Date(r.at).toLocaleString()}
                  </div>
                </div>
                <button type="button"
                        onClick={() => onRestore(r.id)}
                        disabled={busy}
                        data-testid={`${testidPrefix}-revision-restore-${r.id}`}
                        className="hv-pill">
                  <RotateCcw size={10} /> Restore
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* Auth helper — used by every Studio screen. */
export function useAdminToken() {
  const token = (typeof window !== "undefined")
    ? localStorage.getItem(STORAGE_ADMIN) || ""
    : "";
  return token;
}

export async function studioFetch(path, { method = "GET", body, token, signal } = {}) {
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const msg = data && data.detail ? data.detail : `${r.status} error`;
    throw new Error(msg);
  }
  return data;
}
